import {
  CustomIntervalType as CustomIntervalTypeEnum,
  RatelistType as RatelistTypeEnum,
  UnitType,
} from "@sinch/core/entities/serverEnums";
import { CustomIntervalType, RatelistType } from "@sinch/types/sinch.types";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { array, boolean, number, object, ObjectSchema, string } from "yup";
import { useEntityProps } from "../utilsHooks";
import { intervalTypeTimeSchema } from "@sinch/components/application/TimeIntervalSettings/formSchema";

type IntervalType = CustomIntervalType | CustomIntervalTypeEnum;

/**
 * Settings of a break penalty interval, as far as the ratelist builder needs them
 */
interface BreakPenaltySettingsValue {
  componentIds?: string[];
  useCustomComponent?: boolean;
}

type RateIntervalListItem = { id: string; type: IntervalType; settings?: unknown };
/** The rate unit as the builder form holds it */
export type RateUnitValue = { type: string | null; customIntervalId: string | null } | null | undefined;

/**
 * Settings of the break penalty interval the rate unit points at, null for units of any other kind
 */
export const breakPenaltySettings = (
  rateIntervals: readonly RateIntervalListItem[] | undefined,
  unit: RateUnitValue
): BreakPenaltySettingsValue | null => {
  if (unit?.type !== UnitType.Custom || unit.customIntervalId === null) {
    return null;
  }
  const interval = (rateIntervals ?? []).find(
    ({ id, type }) => id === unit.customIntervalId && type === CustomIntervalTypeEnum.BreakPenalty
  );

  return interval ? ((interval.settings ?? {}) as BreakPenaltySettingsValue) : null;
};

/**
 * Whether the selected rate unit points at a break penalty custom interval. Break penalty rows are
 * valued from the rate active during the missed break, so the amount input is meaningless for them.
 */
export const isBreakPenaltyUnit = (
  rateIntervals: readonly RateIntervalListItem[] | undefined,
  unit: RateUnitValue
): boolean => breakPenaltySettings(rateIntervals, unit) !== null;

/**
 * Whether the break penalty interval reports its penalty under the component of the rate it is valued
 * from instead of a custom one. The row's own component is then calculated as well, so it is not for
 * the user to pick. Settings without the flag use a custom component, matching the backend default.
 */
export const isBreakPenaltySourcedComponent = (
  rateIntervals: readonly RateIntervalListItem[] | undefined,
  unit: RateUnitValue
): boolean => breakPenaltySettings(rateIntervals, unit)?.useCustomComponent === false;

/**
 * Custom interval types a ratelist type cannot use, mirrors the backend `RatelistType::getDisallowedCustomUnits()`:
 * transport ratelists have no overtime, no attendance to intersect and no breaks, tag ratelists have no breaks
 * either. The interval picker is filtered server-side by the same rule, this keeps the create dialog from
 * offering an interval the ratelist could never use.
 */
const disallowedCustomIntervalTypes = (
  ratelistType: RatelistType | RatelistTypeEnum | undefined
): readonly CustomIntervalTypeEnum[] => {
  switch (ratelistType) {
    case RatelistTypeEnum.TransportPrice:
    case RatelistTypeEnum.TransportWage:
      return [
        CustomIntervalTypeEnum.DailyOvertime,
        CustomIntervalTypeEnum.WeeklyOvertime,
        CustomIntervalTypeEnum.Intersection,
        CustomIntervalTypeEnum.BreakPenalty,
      ];
    case RatelistTypeEnum.TagPrice:
    case RatelistTypeEnum.TagWage:
      return [CustomIntervalTypeEnum.BreakPenalty];
    default:
      return [];
  }
};

/**
 * Custom interval types supported per ratelist type, see {@link disallowedCustomIntervalTypes}
 */
export const supportedCustomIntervalTypes = (
  ratelistType: RatelistType | RatelistTypeEnum | undefined
): readonly CustomIntervalTypeEnum[] => {
  const disallowed = new Set<CustomIntervalTypeEnum>(disallowedCustomIntervalTypes(ratelistType));
  return Object.values(CustomIntervalTypeEnum).filter((type) => !disallowed.has(type));
};

const nonIntersectionCustomSchemas = {
  [CustomIntervalTypeEnum.BreakPenalty]: object({
    breakAfterMinutes: number().integer().moreThan(0).default(300),
    breakMinutes: number().integer().moreThan(0).default(30),
    penaltyMinutes: number().integer().moreThan(0).default(30),
    componentIds: array()
      .of(string().required())
      // lazy message: the schema is built at import time, before i18next has its resources
      .min(1, () => i18next.t("RateUnit.breakPenaltyComponentsRequired"))
      .default([]),
    useCustomComponent: boolean().default(true),
  }),

  [CustomIntervalTypeEnum.NationalHolidays]: object().shape({
    country: string().required().default("CZ"),
    includeSaturday: boolean().default(false),
    includeSunday: boolean().default(false),
    excludedHolidays: array().of(string().required()).default([]),
  }),

  [CustomIntervalTypeEnum.DailyOvertime]: object({
    limit: number().min(0).default(0),
  }),

  [CustomIntervalTypeEnum.Time]: intervalTypeTimeSchema,

  [CustomIntervalTypeEnum.WeeklyOvertime]: object({
    limit: number().min(0).default(0),
    resetDay: number().min(1).max(7).default(1),
  }),

  [CustomIntervalTypeEnum.WorkedDuration]: object({
    fromAmountOfHours: number().min(0).default(0),
    toAmountOfHours: number()
      .min(0)
      .nullable()
      .when("fromAmountOfHours", ([fromAmount], schema) => (fromAmount ? schema.min(fromAmount) : schema))
      .default(null),
  }),
};

/**
 * One unit inside an intersection composite. Values live inside the free-form settings
 * scalar, so `unitType` / `customType` use the backend enum *values* (lowercase), not the
 * uppercase GraphQL enum names.
 */
export const intersectionChildSchema = object({
  unitType: string<UnitType>().required().default(null),
  customType: string<CustomIntervalTypeEnum>().nullable().default(null),
  settings: object()
    .when(["unitType", "customType"], (values, schema) => {
      const [unitType, customType] = values as [UnitType, CustomIntervalTypeEnum];
      if (unitType !== UnitType.Custom) return schema;
      if (customType !== CustomIntervalTypeEnum.Intersection) {
        return nonIntersectionCustomSchemas[customType];
      }
      return schema;
    })
    .default({}),
});

export const staticCustomIntervalTypeSchemas = {
  ...nonIntersectionCustomSchemas,
  [CustomIntervalTypeEnum.Intersection]: object({
    units: array()
      .of(intersectionChildSchema)
      .min(2)
      .default([
        { unitType: UnitType.Overtime, customType: null, settings: {} },
        {
          unitType: UnitType.Custom,
          customType: CustomIntervalTypeEnum.Time,
          settings: nonIntersectionCustomSchemas[CustomIntervalTypeEnum.Time].getDefault(),
        },
      ]),
  }),
};

const basicTypes = [
  CustomIntervalTypeEnum.NationalHolidays,
  CustomIntervalTypeEnum.NationalHolidays,
  CustomIntervalTypeEnum.DailyOvertime,
  CustomIntervalTypeEnum.Time,
  CustomIntervalTypeEnum.WeeklyOvertime,
  CustomIntervalTypeEnum.WorkedDuration,
];

/**
 * Single source of truth for how a custom interval type looks in the app: label and description.
 *
 * Use these getters wherever a custom interval type is shown.
 * Never hardcode its label and description in a component, change them here.
 * Every value is shown in the "Entity Identity/CustomIntervalType" story.
 *
 * @param ratelistType - when provided, the option list is restricted to types supported
 *   by that ratelist type (e.g. transport ratelists exclude overtime variants).
 */
export const useCustomIntervalType = (ratelistType?: RatelistType | RatelistTypeEnum) => {
  const { t } = useTranslation();

  const operators: Record<IntervalType, { label: string; schema: ObjectSchema<any>; description: string }> = useMemo(
    () => ({
      [CustomIntervalTypeEnum.BreakPenalty]: {
        label: t("CustomUnitType.breakPenalty"),
        description: t("CustomUnitType.breakPenaltyDescription"),
        schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.BreakPenalty],
      },
      [CustomIntervalTypeEnum.Intersection]: {
        label: t("CustomUnitType.intersection"),
        description: t("CustomUnitType.intersectionDescription"),
        schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.Intersection],
      },
      [CustomIntervalTypeEnum.NationalHolidays]: {
        label: t("CustomUnitType.nationalHolidays"),
        description: t("CustomUnitType.nationalHolidaysDescription"),
        schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.NationalHolidays],
      },
      [CustomIntervalTypeEnum.DailyOvertime]: {
        label: t("CustomUnitType.dailyOvertime"),
        description: t("CustomUnitType.dailyOvertimeDescription"),
        schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.DailyOvertime],
      },
      [CustomIntervalTypeEnum.Time]: {
        label: t("CustomUnitType.time"),
        description: t("CustomUnitType.timeDescription"),
        schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.Time],
      },
      [CustomIntervalTypeEnum.WeeklyOvertime]: {
        label: t("CustomUnitType.weeklyOvertime"),
        description: t("CustomUnitType.weeklyOvertimeDescription"),
        schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.WeeklyOvertime],
      },
      [CustomIntervalTypeEnum.WorkedDuration]: {
        label: t("CustomUnitType.workedDuration"),
        description: t("CustomUnitType.workedDurationDescription"),
        schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.WorkedDuration],
      },
    }),
    [t]
  );
  const getProp = useEntityProps(operators);

  const getCustomIntervalLabel = useMemo(() => getProp<string>("label"), [getProp]);
  const getCustomIntervalDescription = useMemo(() => getProp<string>("description"), [getProp]);
  const getCustomIntervalSchema = useMemo(() => getProp<ObjectSchema<any>>("schema"), [getProp]);

  const allowedTypes = useMemo(() => new Set<string>(supportedCustomIntervalTypes(ratelistType)), [ratelistType]);
  const getOptionsList = () =>
    Object.entries(operators)
      .filter(([key]) => allowedTypes.has(key))
      .map(([key, value]) => ({
        value: key,
        label: value.label,
      }));

  return { getCustomIntervalLabel, getCustomIntervalSchema, getOptionsList, getCustomIntervalDescription, basicTypes };
};
